// The client half of the account layer.
//
// Every call here is optional to the application. Signing in buys two things,
// schedules saved off the device and the crew layer, and nothing on the planning
// side waits on any of it. A failed call returns a message rather than throwing,
// because a network that is not there is the normal case on site.

export interface AccountUser {
  id: string;
  username: string;
  displayName: string;
  email: string | null;
  createdAt?: string;
}

export interface AccountProfile {
  visibility: 'private' | 'public';
  lookingForGroup: boolean;
  bio: string;
}

export interface CrewSummary {
  id: string;
  name: string;
  joinCode: string;
  openToNewMembers: boolean;
  visibility: 'private' | 'public';
  blurb: string;
  role: 'owner' | 'member';
}

export interface CloudSaveSummary {
  festivalId: string;
  bytes: number;
  savedAt: string;
}

export interface SessionState {
  user: AccountUser | null;
  profile?: AccountProfile;
  twoFactor?: { enabled: boolean; pending: boolean };
  recoveryCodesRemaining?: number;
  crews?: CrewSummary[];
  cloudSaves?: CloudSaveSummary[];
}

export interface ApiResult<T> {
  ok: boolean;
  status: number;
  data: T | null;
  error?: string;
  field?: string;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  try {
    const response = await fetch(path, {
      credentials: 'same-origin',
      headers: init.body ? { 'content-type': 'application/json' } : undefined,
      ...init,
    });
    const text = await response.text();
    const data = text ? (JSON.parse(text) as T & { error?: string; field?: string }) : null;
    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        data,
        error: data?.error ?? `Request failed with ${response.status}`,
        field: data?.field,
      };
    }
    return { ok: true, status: response.status, data };
  } catch {
    return { ok: false, status: 0, data: null, error: 'No connection to the server right now.' };
  }
}

export const api = {
  session: (signal?: AbortSignal) => call<SessionState>('/api/auth/me', { signal }),

  // `ageConfirmed` and `turnstileToken` are required by the route. Omitting them
  // means every registration is refused before it reaches the database.
  register: (body: {
    username: string;
    password: string;
    email?: string;
    displayName?: string;
    ageConfirmed: boolean;
    turnstileToken?: string;
  }) =>
    call<{ user: AccountUser; recoveryCodes: string[] }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  login: (body: { identifier: string; password: string; code?: string }) =>
    call<{ user?: AccountUser; needsCode?: boolean }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  logout: () => call<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),

  deleteAccount: (password: string) =>
    call<{ ok: boolean; photosRemoved: number; note: string }>('/api/auth/account', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    }),

  forgot: (identifier: string) =>
    call<{ ok: boolean; message: string; delivery?: string; expiresInMinutes: number }>(
      '/api/auth/forgot',
      { method: 'POST', body: JSON.stringify({ identifier }) },
    ),

  resetWithToken: (token: string, newPassword: string) =>
    call<{ user: AccountUser; recoveryCodes: string[] }>('/api/auth/reset', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    }),

  recoverWithCode: (username: string, code: string, newPassword: string) =>
    call<{ user: AccountUser; codesRemaining: number }>('/api/auth/recover', {
      method: 'POST',
      body: JSON.stringify({ username, code, newPassword }),
    }),

  startTotp: () => call<{ secret: string; uri: string }>('/api/auth/totp', { method: 'POST' }),
  confirmTotp: (code: string) =>
    call<{ ok: boolean; enabled: boolean }>('/api/auth/totp', {
      method: 'PUT',
      body: JSON.stringify({ code }),
    }),
  disableTotp: (password: string) =>
    call<{ ok: boolean; enabled: boolean }>('/api/auth/totp', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    }),

  patchProfile: (body: Partial<AccountProfile> & { displayName?: string }) =>
    call<{ profile: AccountProfile & { username: string; displayName: string } }>('/api/profile', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  readSave: (festivalId: string, signal?: AbortSignal) =>
    call<{ save: { payload: unknown; bytes: number; savedAt: string } | null }>(
      `/api/save?festivalId=${encodeURIComponent(festivalId)}`,
      { signal },
    ),

  writeSave: (festivalId: string, payload: unknown, signal?: AbortSignal) =>
    call<{ ok: boolean; bytes: number; savedAt: string }>('/api/save', {
      method: 'PUT',
      body: JSON.stringify({ festivalId, payload }),
      signal,
    }),

  publish: (body: {
    ownerKind: 'user' | 'workspace';
    workspaceId?: string;
    festivalId: string;
    dayId: string;
    dayLabel: string;
    title: string;
    blurb: string;
    sets: {
      setId: string;
      artistId: string;
      artistName: string;
      stageId: string;
      startTs: number;
      endTs: number;
      mode: string;
    }[];
  }) => call<{ ok: boolean; id: string; setCount: number }>('/api/publish', {
    method: 'PUT',
    body: JSON.stringify(body),
  }),

  unpublish: (dayId: string, ownerKind: 'user' | 'workspace', workspaceId?: string) =>
    call<{ ok: boolean }>(
      `/api/publish?dayId=${encodeURIComponent(dayId)}&ownerKind=${ownerKind}` +
        (workspaceId ? `&workspaceId=${encodeURIComponent(workspaceId)}` : ''),
      { method: 'DELETE' },
    ),

  art: (params: Record<string, string> = {}) =>
    call<{ pieces: ArtPiece[] }>(`/api/art?${new URLSearchParams(params).toString()}`),

  artTags: () => call<{ tags: { tag: string; count: number }[] }>('/api/art/tags'),

  postArt: (body: { photoId: string; title: string; medium?: string; body?: string; tags: string[] }) =>
    call<{ id: string; status: string; tags: string[]; note: string | null }>('/api/art', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  patchArt: (id: string, body: Record<string, unknown>) =>
    call<{ ok: boolean; status: string; note: string | null }>(`/api/art/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  deleteArt: (id: string) =>
    call<{ ok: boolean }>(`/api/art/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  contributors: () => call<{ contributors: Contributor[] }>('/api/art/contributors'),

  mySubmissions: () => call<{ submissions: ArtSubmission[] }>('/api/art/submissions'),

  submitArt: (body: {
    pieceId: string;
    placement: string;
    attribution: string;
    notes?: string;
    contact?: string;
  }) =>
    call<{ ok: boolean; id: string; delivered: boolean; note: string }>('/api/art/submissions', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  withdrawSubmission: (id: string) =>
    call<{ ok: boolean }>(`/api/art/submissions/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  discover: (params: Record<string, string>) =>
    call<{ results: DiscoverResult[]; count: number; sort: string }>(
      `/api/discover?${new URLSearchParams(params).toString()}`,
    ),

  createCrew: (body: { name: string; festivalId: string; blurb?: string; openToNewMembers?: boolean; visibility?: string }) =>
    call<CrewPayload>('/api/crew', { method: 'POST', body: JSON.stringify(body) }),

  joinCrew: (joinCode: string) =>
    call<CrewPayload>('/api/crew/join', { method: 'POST', body: JSON.stringify({ joinCode }) }),

  readCrew: (id: string) => call<CrewPayload>(`/api/crew/${encodeURIComponent(id)}`),

  patchCrew: (id: string, body: Record<string, unknown>) =>
    call<CrewPayload>(`/api/crew/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  addCrewMember: (id: string, username: string) =>
    call<CrewPayload>(`/api/crew/${encodeURIComponent(id)}/members`, {
      method: 'POST',
      body: JSON.stringify({ username }),
    }),

  askToJoin: (id: string, message: string) =>
    call<{ ok: boolean; status: string }>(`/api/crew/${encodeURIComponent(id)}/requests`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),

  answerRequest: (id: string, requestId: string, status: 'accepted' | 'declined') =>
    call<CrewPayload>(`/api/crew/${encodeURIComponent(id)}/requests/${encodeURIComponent(requestId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
};

export interface ArtPiece {
  id: string;
  title: string;
  medium: string | null;
  body: string | null;
  createdAt: string;
  editedAt: string | null;
  status: 'published' | 'flagged' | 'blocked' | 'removed';
  url: string;
  photoId: string;
  artist: string;
  artistName: string;
  mine: boolean;
  tags: string[];
}

export interface Contributor {
  id: string;
  pieceId: string;
  title: string;
  medium: string | null;
  url: string;
  /** Exactly the words the artist asked to be credited with. */
  credit: string;
  usedWhere: string | null;
  acceptedAt: string | null;
}

export interface ArtSubmission {
  id: string;
  title: string;
  url: string;
  status: 'open' | 'accepted' | 'declined' | 'withdrawn';
  placement: string;
  attribution: string;
  notes: string | null;
  usedWhere: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

export interface DiscoverResult {
  id: string;
  ownerKind: 'user' | 'workspace';
  ownerId: string;
  ownerLabel: string;
  ownerUsername: string | null;
  dayId: string;
  dayLabel: string;
  title: string;
  blurb: string;
  memberCount: number;
  openToJoin: boolean;
  setCount: number;
  firstStartTs: number | null;
  lastEndTs: number | null;
  publishedAt: string;
  overlap: number;
  sets: {
    setId: string;
    artistId: string;
    artistName: string;
    stageId: string;
    startTs: number;
    endTs: number;
    mode: string;
  }[];
}

export interface CrewPayload {
  crew: {
    id: string;
    name: string;
    joinCode: string;
    blurb: string;
    visibility: 'private' | 'public';
    openToNewMembers: boolean;
    festivalId: string;
  };
  members: {
    userId: string;
    username: string | null;
    displayName: string;
    color: string;
    role: 'owner' | 'member';
  }[];
  requests: {
    id: string;
    userId: string;
    username: string;
    displayName: string;
    message: string;
    status: 'open' | 'accepted' | 'declined';
    createdAt: string;
  }[];
}
